import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { safeLocalStorage } from '@hooks/useStorage'

interface Point {
  x: number
  y: number
}

interface TrackedPoint extends Point {
  timestamp: number
}

interface SubmenuTarget {
  id: string
  top: number
}

interface SidebarContextValue {
  isExpanded: boolean
  toggleSidebar: () => void
  activeSubmenu: string | null
  submenuTop: number
  openSubmenu: (id: string, top?: number) => void
  closeSubmenu: () => void
  sidebarWidth: number
  adjustSubmenuPosition: (submenuHeight: number, currentTop: number) => void

  trackPointer: (x: number, y: number) => void
  requestSubmenuChange: (target: SubmenuTarget | null, point: Point) => void
  cancelPendingSubmenuChange: () => void
  setSubmenuElement: (element: HTMLDivElement | null) => void
}

const SAFE_TRIANGLE_DELAY = 300
const POINTER_HISTORY_MS = 150
const TRIANGLE_VERTICAL_PADDING = 16

const isPointInTriangle = (point: Point, a: Point, b: Point, c: Point) => {
  const sign = (p1: Point, p2: Point, p3: Point) =>
    (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y)

  const d1 = sign(point, a, b)
  const d2 = sign(point, b, c)
  const d3 = sign(point, c, a)

  const hasNegative = d1 < 0 || d2 < 0 || d3 < 0
  const hasPositive = d1 > 0 || d2 > 0 || d3 > 0

  return !(hasNegative && hasPositive)
}

export const SidebarContext = createContext<SidebarContextValue | undefined>(undefined)

export const SidebarProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [isExpanded, setIsExpanded] = useState(
    () => safeLocalStorage.getItem('sidebar-expanded') === 'true'
  )
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null)
  const [submenuTop, setSubmenuTop] = useState(0)

  const pointerHistoryRef = useRef<TrackedPoint[]>([])
  const submenuElementRef = useRef<HTMLDivElement | null>(null)
  const pendingHoverTimerRef = useRef<number | null>(null)

  const sidebarWidth = isExpanded ? 236 : 64

  const cancelPendingSubmenuChange = useCallback(() => {
    if (pendingHoverTimerRef.current !== null) {
      window.clearTimeout(pendingHoverTimerRef.current)
      pendingHoverTimerRef.current = null
    }
  }, [])

  const trackPointer = useCallback((x: number, y: number) => {
    const timestamp = performance.now()

    pointerHistoryRef.current = [
      ...pointerHistoryRef.current,
      {
        x,
        y,
        timestamp,
      },
    ]
      .filter((point) => timestamp - point.timestamp <= POINTER_HISTORY_MS)
      .slice(-10)
  }, [])

  const setSubmenuElement = useCallback((element: HTMLDivElement | null) => {
    submenuElementRef.current = element
  }, [])

  const isMovingTowardSubmenu = useCallback((currentPoint: Point) => {
    const submenu = submenuElementRef.current

    if (!submenu) {
      return false
    }

    const rect = submenu.getBoundingClientRect()
    const history = pointerHistoryRef.current

    if (!history.length) {
      return false
    }

    const previousPoint = [...history]
      .reverse()
      .find(
        (point) => Math.abs(currentPoint.x - point.x) > 4 || Math.abs(currentPoint.y - point.y) > 4
      )

    if (!previousPoint) {
      return false
    }

    const submenuIsOnRight = rect.left > previousPoint.x
    const isMovingHorizontallyTowardSubmenu = submenuIsOnRight
      ? currentPoint.x > previousPoint.x
      : currentPoint.x < previousPoint.x

    if (!isMovingHorizontallyTowardSubmenu) {
      return false
    }

    const submenuEdgeX = submenuIsOnRight ? rect.left : rect.right

    const submenuTopCorner: Point = {
      x: submenuEdgeX,
      y: rect.top - TRIANGLE_VERTICAL_PADDING,
    }

    const submenuBottomCorner: Point = {
      x: submenuEdgeX,
      y: rect.bottom + TRIANGLE_VERTICAL_PADDING,
    }

    return isPointInTriangle(currentPoint, previousPoint, submenuTopCorner, submenuBottomCorner)
  }, [])

  const applySubmenuChange = useCallback((target: SubmenuTarget | null) => {
    if (!target) {
      setActiveSubmenu(null)
      return
    }

    setActiveSubmenu(target.id)
    setSubmenuTop(target.top)
  }, [])

  const requestSubmenuChange = useCallback(
    (target: SubmenuTarget | null, point: Point) => {
      cancelPendingSubmenuChange()

      if (!activeSubmenu) {
        applySubmenuChange(target)
        return
      }

      if (target?.id === activeSubmenu) {
        return
      }

      if (isMovingTowardSubmenu(point)) {
        pendingHoverTimerRef.current = window.setTimeout(() => {
          applySubmenuChange(target)
          pendingHoverTimerRef.current = null
        }, SAFE_TRIANGLE_DELAY)

        return
      }

      applySubmenuChange(target)
    },
    [activeSubmenu, applySubmenuChange, cancelPendingSubmenuChange, isMovingTowardSubmenu]
  )

  const toggleSidebar = useCallback(() => {
    cancelPendingSubmenuChange()

    setActiveSubmenu(null)

    setIsExpanded((prev) => {
      safeLocalStorage.setItem('sidebar-expanded', String(!prev))
      return !prev
    })
  }, [cancelPendingSubmenuChange])

  const openSubmenu = useCallback(
    (id: string, top?: number) => {
      cancelPendingSubmenuChange()

      setActiveSubmenu(id)

      if (top !== undefined) {
        setSubmenuTop(top)
      }
    },
    [cancelPendingSubmenuChange]
  )

  const closeSubmenu = useCallback(() => {
    cancelPendingSubmenuChange()
    setActiveSubmenu(null)
  }, [cancelPendingSubmenuChange])

  const adjustSubmenuPosition = useCallback((submenuHeight: number, currentTop: number) => {
    const viewportHeight = window.innerHeight
    const wouldOverflow = currentTop + submenuHeight > viewportHeight

    if (wouldOverflow) {
      setSubmenuTop(viewportHeight - submenuHeight)
    }
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeSubmenu()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [closeSubmenu])

  useEffect(() => {
    return () => {
      cancelPendingSubmenuChange()
    }
  }, [cancelPendingSubmenuChange])

  return (
    <SidebarContext.Provider
      value={{
        isExpanded,
        toggleSidebar,
        activeSubmenu,
        submenuTop,
        openSubmenu,
        closeSubmenu,
        sidebarWidth,
        adjustSubmenuPosition,
        trackPointer,
        requestSubmenuChange,
        cancelPendingSubmenuChange,
        setSubmenuElement,
      }}
    >
      {children}
    </SidebarContext.Provider>
  )
}

export const useSidebar = (): SidebarContextValue => {
  const ctx = useContext(SidebarContext)

  if (!ctx) {
    throw new Error('useSidebar must be inside <SidebarProvider>')
  }

  return ctx
}
